import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Paper,
  Alert,
  Collapse,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Accordion,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Switch,
  FormControlLabel,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  InputAdornment,
  Tab,
  Tabs,
  Checkbox,
  ListItemText,
  Radio
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CodeIcon from '@mui/icons-material/Code';
import SendIcon from '@mui/icons-material/Send';
// Importer l'instance axios configurée avec les intercepteurs d'authentification
import api from '../../services/auth';
import { HIDE_INCOMPLETE_FEATURES, fetchLLMConfig } from '../../services/config';
import Autocomplete from '@mui/material/Autocomplete';
import EmailAssistant from '../../components/EmailAssistantNew';
import CampaignGraphBuilder from '../../components/CampaignGraphBuilder';

const steps = [
  "Objectif",
  "Cible",
  "Contenu",
  "Timing",
  "Workflow",
  "Résumé"
];

// Styles CSS personnalisés basés sur le template HTML
const customStyles = {
  root: {
    fontFamily: "'Source Sans 3', 'Inter', sans-serif",
    backgroundColor: '#f1f5f9',
    color: '#4B5563',
    minHeight: '100vh',
    padding: '1rem',
    '@media (min-width: 640px)': {
      padding: '2rem'
    }
  },
  container: {
    maxWidth: '80rem',
    margin: '0 auto',
    backgroundColor: 'white',
    borderRadius: '0.75rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  },
  header: {
    padding: '1.5rem',
    borderBottom: '1px solid #e2e8f0',
    '@media (min-width: 640px)': {
      padding: '2rem'
    }
  },
  headerTitle: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
    fontFamily: "'Source Sans 3', 'Inter', sans-serif"
  },
  headerSubtitle: {
    marginTop: '0.25rem',
    color: '#6b7280',
    fontFamily: "'Source Sans 3', 'Inter', sans-serif"
  },
  navigation: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '0.5rem',
    padding: '1rem',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    '@media (min-width: 640px)': {
      gap: '1rem'
    }
  },
  stepButton: {
    position: 'relative',
    padding: '0.5rem 1.25rem',
    fontWeight: 500,
    color: '#6b7280',
    transition: 'all 0.3s ease',
    borderRadius: '9999px',
    fontSize: '0.9rem',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    fontFamily: "'Source Sans 3', 'Inter', sans-serif",
    '&.active': {
      color: '#fff',
      backgroundColor: '#007B8A'
    }
  },
  content: {
    padding: '1.5rem',
    '@media (min-width: 640px)': {
      padding: '2rem'
    }
  },
  stepTitle: {
    fontSize: '1.25rem',
    fontWeight: 500,
    color: '#1f2937',
    marginBottom: '1.25rem',
    fontFamily: "'Source Sans 3', 'Inter', sans-serif"
  },
  formInput: {
    width: '100%',
    border: '1px solid #e2e8f0',
    borderRadius: '0.5rem',
    padding: '0.75rem 1rem',
    transition: 'all 0.2s ease',
    fontFamily: "'Source Sans 3', sans-serif",
    '&:focus': {
      outline: 'none',
      borderColor: '#007B8A',
      boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.15)'
    }
  },
  btn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.65rem 1.25rem',
    borderRadius: '0.5rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: 'none',
    fontFamily: "'Source Sans 3', sans-serif"
  },
  btnPrimary: {
    backgroundColor: '#007B8A',
    color: 'white',
    '&:hover': {
      backgroundColor: '#005D6B',
      transform: 'translateY(-1px)'
    }
  },
  btnSecondary: {
    backgroundColor: '#F1F5F9',
    color: '#4B5563',
    border: '1px solid #e2e8f0',
    '&:hover': {
      backgroundColor: '#E2E8F0'
    }
  },
  btnSuggest: {
    backgroundColor: '#10B981',
    color: 'white',
    '&:hover': {
      backgroundColor: '#059669'
    }
  },
  card: {
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '0.75rem',
    padding: '1.25rem'
  }
};

export default function PlanificationCampagne() {
  const location = useLocation();
  const navigate = useNavigate();
  const suggestions = location.state?.suggestions;
  const campaignName = location.state?.campaignName;

  const [activeStep, setActiveStep] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [segmentSuggestions, setSegmentSuggestions] = useState([]);
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [isLoadingSegments, setIsLoadingSegments] = useState(false);
  const [loadingSegmentIds, setLoadingSegmentIds] = useState([]);
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mauticAvailable, setMauticAvailable] = useState(true);
  const [form, setForm] = useState({
    objectif: "",
    canal: "mautic_email", // Canal principal par défaut
    cible: "",
    selectedSegments: [],
    contenu: "",
    selectedEmails: [],
    loadingEmailIds: [], // Pour suivre les emails en cours de création
    timing: "",
    workflow: "",
    resume: "",
    useExistingSegment: false,
    useExistingEmail: false,
    startDate: '',
    endDate: '',
    isPublished: false,
    timezone: '+01:00',
    workflowDescription: '',
    campaignJson: null,
    visualGraph: null, // Graphique de l'éditeur visuel séparé
    workflowMode: HIDE_INCOMPLETE_FEATURES ? 'nl' : 'nl' // Mode par défaut pour les onglets du workflow
  });
  
  // États pour les segments et emails existants dans Mautic
  const [existingSegments, setExistingSegments] = useState([]);
  const [existingEmails, setExistingEmails] = useState([]);
  const [isLoadingExistingSegments, setIsLoadingExistingSegments] = useState(false);
  const [isLoadingExistingEmails, setIsLoadingExistingEmails] = useState(false);
  const [segmentSearchText, setSegmentSearchText] = useState('');
  const [emailSearchText, setEmailSearchText] = useState('');
  const [filteredSegments, setFilteredSegments] = useState([]);
  const [filteredEmails, setFilteredEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customCampaignName, setCustomCampaignName] = useState("");
  const [userCredentials, setUserCredentials] = useState({
    brand: "",
    mautic_url: "",
    mautic_username: "",
    mautic_password: ""
  });
  const [emailEditorOpen, setEmailEditorOpen] = useState(false);
  const [emailToEdit, setEmailToEdit] = useState(null);
  
  // Référence pour le menu de sélection des segments
  const segmentSelectRef = React.useRef(null);
  const emailSelectRef = React.useRef(null);

  // Fonction pour gérer le changement de mode de workflow
  const handleWorkflowModeChange = (newMode) => {
    setForm(prev => {
      // Si on passe du mode visuel à NL, sauvegarder le graphique visuel
      if (prev.workflowMode === 'visual' && newMode === 'nl') {
        return {
          ...prev,
          workflowMode: newMode,
          visualGraph: prev.campaignJson || prev.visualGraph
        };
      }
      // Si on passe du mode NL au visuel, restaurer le graphique visuel
      else if (prev.workflowMode === 'nl' && newMode === 'visual') {
        return {
          ...prev,
          workflowMode: newMode,
          campaignJson: prev.visualGraph || prev.campaignJson
        };
      }
      // Sinon, juste changer le mode
      else {
        return {
          ...prev,
          workflowMode: newMode
        };
      }
    });
  };

  // Charger les segments et emails existants au chargement du composant
  useEffect(() => {
    fetchUserCredentials();
    fetchExistingSegments();
    fetchExistingEmails();
  }, []);

  // Réinitialiser la recherche de segments quand les segments changent
  useEffect(() => {
    setFilteredSegments(existingSegments);
  }, [existingSegments]);

  // Fonction pour récupérer les identifiants utilisateur depuis le backend
  const fetchUserCredentials = async () => {
    try {
      const response = await api.get('/auth/credentials');
      if (response.data.success && response.data.credentials) {
        setUserCredentials(response.data.credentials);
        setMauticAvailable(
          !!response.data.credentials.mautic_url && 
          !!response.data.credentials.mautic_username && 
          !!response.data.credentials.mautic_password
        );
      } else {
        setMauticAvailable(false);
      }
    } catch (err) {
      setMauticAvailable(false);
    }
  };

  // Fonction pour vérifier la disponibilité des credentials Mautic
  const checkMauticCredentials = () => {
    return userCredentials.mautic_url && userCredentials.mautic_username && userCredentials.mautic_password;
  };

  // Pré-remplir le formulaire avec les suggestions de l'IA si disponibles
  useEffect(() => {
    // Gère les suggestions reçues (tableau de chaînes) pour éviter les erreurs.
    if (suggestions && Array.isArray(suggestions) && suggestions.length > 0) {
      // Construit une chaîne de caractères à partir des suggestions pour l'objectif.
      const objectifText = "Optimisation de la campagne basée sur l'analyse IA des points suivants:\n- " + suggestions.join('\n- ');
      
      setForm(prev => ({
        ...prev,
        objectif: objectifText, // Pré-remplit l'objectif
        cible: "", // Réinitialise les champs dépendants
        workflow: "Workflow à définir basé sur les recommandations.",
        contenu: "",
        timing: "",
      }));
    }
    // Si 'suggestions' n'est pas un tableau valide, ne fait rien pour éviter un crash.
  }, [suggestions]);

  // Fonction pour récupérer les segments existants depuis Mautic
  const fetchExistingSegments = async () => {
    setIsLoadingExistingSegments(true);
    setError('');
    
    try {
      const response = await api.get('/campaigns/segments');
      
      if (response.data.success && response.data.segments) {
        // Tri des segments par date de création (du plus récent au moins récent)
        const sortedSegments = [...response.data.segments].sort((a, b) => {
          const dateA = new Date(a.dateAdded || a.dateModified || 0);
          const dateB = new Date(b.dateAdded || b.dateModified || 0);
          return dateB - dateA; // Ordre décroissant (plus récent en premier)
        });
        
        setExistingSegments(sortedSegments);
        setFilteredSegments(sortedSegments);
        setMauticAvailable(true);
      } else {
        if (response.data.message && response.data.message.includes('Mautic integration is not available')) {
          setMauticAvailable(false);
        }
      }
    } catch (err) {
      if (err.message && err.message.includes('MAUTIC_USERNAME is not set')) {
        setMauticAvailable(false);
      }
    } finally {
      setIsLoadingExistingSegments(false);
    }
  };

  // Fonction pour récupérer les templates d'email existants depuis Mautic
  const fetchExistingEmails = async () => {
    setIsLoadingExistingEmails(true);
    setError('');
    
    try {
      const response = await api.get('/campaigns/email-templates');
      
      if (response.data.success && response.data.templates) {
        // Tri des templates par date de création (du plus récent au moins récent)
        const sortedEmails = [...response.data.templates].sort((a, b) => {
          const dateA = new Date(a.dateAdded || a.dateModified || 0);
          const dateB = new Date(b.dateAdded || b.dateModified || 0);
          return dateB - dateA; // Ordre décroissant (plus récent en premier)
        });
        
        setExistingEmails(sortedEmails);
        setFilteredEmails(sortedEmails);
        setMauticAvailable(true);
      } else {
        if (response.data.message && response.data.message.includes('Mautic integration is not available')) {
          setMauticAvailable(false);
        }
      }
    } catch (err) {
      if (err.message && err.message.includes('MAUTIC_USERNAME is not set')) {
        setMauticAvailable(false);
      }
    } finally {
      setIsLoadingExistingEmails(false);
    }
  };

  // Fonction pour rechercher parmi les segments existants
  const handleSegmentSearch = (e) => {
    const searchValue = e.target.value;
    setSegmentSearchText(searchValue);
    
    // Filtrer immédiatement en fonction de la saisie
    if (!searchValue.trim()) {
      setFilteredSegments(existingSegments);
    } else {
      const filtered = existingSegments.filter(segment => 
        segment.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        (segment.description && segment.description.toLowerCase().includes(searchValue.toLowerCase()))
      );
      setFilteredSegments(filtered);
    }
  };

  // Fonction pour rechercher parmi les templates d'email existants
  const handleEmailSearch = (e) => {
    const searchValue = e.target.value;
    setEmailSearchText(searchValue);
    
    if (!searchValue.trim()) {
      setFilteredEmails(existingEmails);
      return;
    }
    
    const filtered = existingEmails.filter(email => 
      email.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      (email.subject && email.subject.toLowerCase().includes(searchValue.toLowerCase()))
    );
    
    setFilteredEmails(filtered);
  };

  

  const suggestSegments = async () => {
    if (!form.objectif.trim()) {
      setError('Veuillez définir un objectif avant de générer des segments');
      return;
    }

    setIsLoadingSegments(true);
    setError('');
    setSegmentSuggestions([]);

    try {
      // Fetch LLM configuration from backend
      const llmConfig = await fetchLLMConfig();
      
      // Prompt concis focalisé sur les propriétés supportées pour réduire les timeouts
      const formattedQuery = [
        `Objectif: ${form.objectif}`,
        "Consigne: Suggère 3 à 5 segments en FRANÇAIS en utilisant UNIQUEMENT les champs existants du système. N'invente aucun champ.",
        "Chaque segment doit avoir: name, description, et une configuration Unomi valide basée sur ces champs."
      ].join('\n');

      // Use LLM configuration from backend
      const primaryReq = {
        query: formattedQuery,
        provider: llmConfig.provider,
        model: llmConfig.model,
        includeExisting: true
      };

      let response = await api.post('/segments/suggest', primaryReq);

      if (response?.data?.success && Array.isArray(response.data.suggestions) && response.data.suggestions.length > 0) {
        const formattedSuggestions = response.data.suggestions.map(suggestion => ({
          ...suggestion,
          isExisting: suggestion.mautic_segment_id ? true : false
        }));
        setSegmentSuggestions(formattedSuggestions);
        setSuccess('Segments suggérés générés avec succès');
        setMauticAvailable(!response.data.message?.includes('Mautic integration is not available'));
      } else {
        setError(response?.data?.message || "Aucune suggestion de segment trouvée. Précisez l'objectif avec des critères mesurables (ex: inactifs > 90 jours, pays = FR).");
      }
    } catch (err) {
      setError(err.message || 'Échec de la génération des suggestions de segments. Veuillez réessayer avec un objectif plus détaillé.');
      if (err.message && err.message.includes('MAUTIC_USERNAME is not set')) {
        setMauticAvailable(false);
      }
    } finally {
      setIsLoadingSegments(false);
    }
  };

  // Fonction pour sélectionner ou désélectionner un segment
  const handleSelectSegment = (segment) => {
    setForm(prev => {
      const isSelected = prev.selectedSegments.some(s => s.name === segment.name);
      let newSelectedSegments;
      
      if (isSelected) {
        // Désélectionner le segment
        newSelectedSegments = prev.selectedSegments.filter(s => s.name !== segment.name);
      } else {
        // Sélectionner le segment
        newSelectedSegments = [...prev.selectedSegments, segment];
      }

      return {
        ...prev,
        selectedSegments: newSelectedSegments,
        useExistingSegment: false
      };
    });
  };
  
  // La fonction handleSelectExistingSegment est déjà définie plus haut dans le code
  
  // Fonction pour sélectionner ou désélectionner un email
  const handleSelectEmail = (email) => {
    setForm(prev => {
      const isSelected = prev.selectedEmails.some(e => e.name === email.name);
      let newSelectedEmails;
      
      if (isSelected) {
        // Désélectionner l'email
        newSelectedEmails = prev.selectedEmails.filter(e => e.name !== email.name);
      } else {
        // Sélectionner l'email
        newSelectedEmails = [...prev.selectedEmails, email];
      }

      return {
        ...prev,
        selectedEmails: newSelectedEmails
      };
    });
  };
  
  // La fonction handleSelectExistingEmail est déjà définie plus haut dans le code
  
  // Gestion du changement de fuseau horaire
  const handleTimezoneChange = (e) => {
    setForm(prev => ({
      ...prev,
      timezone: e.target.value
    }));
  };

  // Formater une date pour Mautic
  const formatDateForMautic = (dateTimeValue) => {
    if (!dateTimeValue) return '';
    return dateTimeValue.replace('T', ' ') + ':00';
  };
  
  // Fonction pour ajuster la date en fonction du fuseau horaire
  const adjustDateForTimezone = (dateTimeValue, timezone) => {
    if (!dateTimeValue) return '';
    try {
      // Normaliser la valeur de date vers ISO-like (YYYY-MM-DDTHH:MM:SS)
      let normalized = dateTimeValue;
      if (dateTimeValue.includes(' ')) normalized = dateTimeValue.replace(' ', 'T');
      if (!normalized.includes('T')) normalized = `${normalized}T00:00:00`;

      const localDate = new Date(normalized);
      if (isNaN(localDate.getTime())) {
        return dateTimeValue;
      }

      // Calculer l'offset en minutes à partir de "UTC", "+HH:MM" ou "-HH:MM". Pour les autres valeurs, on suppose UTC.
      let offsetMinutes = 0;
      if (timezone && /^([+-])(\d{2}):(\d{2})$/.test(timezone)) {
        const [, sign, hh, mm] = timezone.match(/^([+-])(\d{2}):(\d{2})$/) || [];
        const minutes = parseInt(hh, 10) * 60 + parseInt(mm, 10);
        offsetMinutes = sign === '-' ? -minutes : minutes;
      } else if (timezone === 'UTC') {
        offsetMinutes = 0;
      } else if (typeof timezone === 'string' && timezone.startsWith('+') || timezone.startsWith('-')) {
        // Fallback simple pour formats comme +01:00 (déjà couvert) ou +01
        const hours = parseInt(timezone, 10);
        offsetMinutes = isNaN(hours) ? 0 : hours * 60;
      } else {
        // Non géré (ex: Europe/Paris) -> fallback UTC
        offsetMinutes = 0;
      }

      // Convertir l'heure locale saisie vers UTC attendu par backend: UTC = local - offset
      const utcMs = localDate.getTime() - offsetMinutes * 60 * 1000;
      const utcDate = new Date(utcMs);

      const pad = (num) => String(num).padStart(2, '0');
      const formatted = `${utcDate.getUTCFullYear()}-${pad(utcDate.getUTCMonth() + 1)}-${pad(utcDate.getUTCDate())} ${pad(utcDate.getUTCHours())}:${pad(utcDate.getUTCMinutes())}:00`;
      return formatted;
    } catch (err) {
      return dateTimeValue;
    }
  };

  // Format helper for displaying dates nicely in FR from 'YYYY-MM-DD HH:MM:SS'
  const formatDisplayDate = (dateTimeValue) => {
    if (!dateTimeValue) return '';
    try {
      const normalized = dateTimeValue.includes('T') ? dateTimeValue : dateTimeValue.replace(' ', 'T');
      const d = new Date(normalized);
      if (isNaN(d.getTime())) return dateTimeValue;
      return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return dateTimeValue;
    }
  };

  // Construit une description narrative détaillée à partir d'un JSON de campagne
  const buildWorkflowNarrative = (campaignData) => {
    try {
      if (!campaignData) return '';
      // Supporte deux schémas: { campaign: {...} } ou top-level {...}
      const c = campaignData.campaign || campaignData;
      const lines = [];

      if (c.name) {
        lines.push(`Campagne: ${c.name}`);
      }

      if (c.publishUp || c.publishDown) {
        const start = c.publishUp ? formatDisplayDate(c.publishUp) : 'Non défini';
        const end = c.publishDown ? formatDisplayDate(c.publishDown) : 'Non définie';
        lines.push(`Période: Début ${start} • Fin ${end}`);
      }

      const events = Array.isArray(c.events) ? c.events : [];
      if (events.length === 0) return lines.join('\n');

      events.forEach((event, index) => {
        const stepTitle = `Étape ${index + 1}`;
        const stepLines = [];

        const eventName = event.name || event.type || 'Événement';
        stepLines.push(`${eventName}`);

        // Timing/trigger
        if (event.timing && event.timing.type) {
          if (event.timing.type === 'interval') {
            const unit = event.timing.unit || 'jours';
            stepLines.push(`Timing: Attente de ${event.timing.interval} ${unit}`);
          } else if (event.timing.type === 'date') {
            stepLines.push(`Timing: À la date ${formatDisplayDate(event.timing.date)}`);
          } else if (event.timing.type === 'immediate') {
            stepLines.push('Timing: Exécution immédiate');
          }
        } else if (event.triggerMode === 'date' && event.triggerDate) {
          stepLines.push(`Timing: Déclenchement le ${formatDisplayDate(event.triggerDate)}`);
        }

        // Conditions
        if (Array.isArray(event.conditions) && event.conditions.length > 0) {
          const conds = event.conditions.map((cond) => cond.description || cond.type || 'Condition').join(', ');
          stepLines.push(`Conditions: ${conds}`);
        }

        // Actions
        if (Array.isArray(event.actions) && event.actions.length > 0) {
          const actionDescriptions = event.actions.map((a) => {
            const label = a.description || a.type || 'Action';
            if (a.type === 'sendEmail' && a.emailName) {
              return `Envoi de l'email « ${a.emailName} »`;
            }
            // Supporte typages alternatifs
            if ((a.type === 'email.send' || a.type === 'emailSend') && (a.emailName || a.email || a.name)) {
              return `Envoi de l'email « ${a.emailName || a.email || a.name} »`;
            }
            if (a.type === 'addToSegment' && a.segmentName) {
              return `Ajout au segment « ${a.segmentName} »`;
            }
            if (a.type === 'removeFromSegment' && a.segmentName) {
              return `Retrait du segment « ${a.segmentName} »`;
            }
            if ((a.type === 'emailOpened' || a.type === 'openCondition') && a.emailName) {
              return `Décision: email « ${a.emailName} » ouvert`;
            }
            if ((a.type === 'emailClicked' || a.type === 'clickCondition') && a.emailName) {
              return `Décision: clic dans l'email « ${a.emailName} »`;
            }
            return label;
          });
          stepLines.push(`Actions: ${actionDescriptions.join('; ')}`);
        }

        // Branches courantes (si le schéma les expose)
        if (Array.isArray(event.branches) && event.branches.length > 0) {
          const branches = event.branches.map((b) => {
            const name = b.name || b.type || 'Branche';
            if (b.condition) {
              const cond = b.condition.description || b.condition.type || 'condition';
              return `${name}: ${cond}`;
            }
            return name;
          });
          stepLines.push(`Branches: ${branches.join(' | ')}`);
        }

        lines.push(`${stepTitle}:\n${stepLines.join('\n')}`);
      });

      return lines.join('\n\n');
    } catch (_) {
      return '';
    }
  };

  // Gestion du changement de date (début ou fin)
  const handleDateChange = (field, e) => {
    const newValue = e.target.value;
    setForm(prev => {
      // Si c'est la date de fin, vérifier qu'elle est après la date de début
      if (field === 'endDate' && prev.startDate && new Date(newValue) <= new Date(prev.startDate)) {
        setError('La date de fin doit être postérieure à la date de début');
        setTimeout(() => setError(''), 3000);
        return prev;
      } 
      
      // Si c'est la date de début, vérifier qu'elle est avant la date de fin
      if (field === 'startDate' && prev.endDate && new Date(prev.endDate) <= new Date(newValue)) {
        setError('La date de début doit être antérieure à la date de fin');
        setTimeout(() => setError(''), 3000);
        return prev;
      }
      
      // Formatter la date pour Mautic mais ne pas encore l'ajuster au fuseau horaire
      // L'ajustement sera fait au moment de l'envoi
      const formattedDate = formatDateForMautic(newValue);
      
      return {
        ...prev,
        [field]: formattedDate
      };
    });
  };

  // Gestion du changement de l'état de publication
  const handlePublishToggle = (e) => {
    const isChecked = e.target.checked;
    setForm(prev => ({
      ...prev,
      isPublished: isChecked,
      startDate: isChecked ? prev.startDate : '',
      endDate: isChecked ? prev.endDate : ''
    }));
  };
  
  // Génération du workflow de la campagne basé sur l'objectif, les segments et l'email
  const generateCampaignWorkflow = async () => {
    if (form.selectedSegments.length === 0 || form.selectedEmails.length === 0) {
      setError('Veuillez sélectionner au moins un segment et un email avant de générer le workflow');
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      // Utiliser les identifiants Mautic récupérés du backend au lieu du localStorage
      const mauticUrl = userCredentials.mautic_url || '';
      const mauticUsername = userCredentials.mautic_username || '';
      const mauticPassword = userCredentials.mautic_password || '';
      
      // Préparation des données pour la génération du workflow
      // Ajuster les dates pour le prompt en fonction du fuseau horaire
      const adjustedStartDate = form.isPublished ? adjustDateForTimezone(form.startDate, form.timezone) : '';
      const adjustedEndDate = form.isPublished ? adjustDateForTimezone(form.endDate, form.timezone) : '';
      
      const prompt = `Génère un workflow de campagne marketing avec les paramètres suivants :
        - Objectif: ${form.objectif}
        - Segments cibles: ${form.selectedSegments.map(segment => segment.name).join(', ')} ${form.selectedSegments.map(segment => segment.description).filter(desc => desc).join(', ')}
        - Emails cibles: ${form.selectedEmails.map(email => `${email.name}${email.description ? ` (${email.description})` : ''}`).join(', ')}
        - Période: ${form.isPublished ? `Du ${adjustedStartDate} au ${adjustedEndDate} (UTC)` : 'Pas de programmation (lancement direct)'}
        - CONTRAINTE CRUCIALE: Si la campagne est programmée, inclure explicitement dans la structure générée les champs publishUp et publishDown au format 'YYYY-MM-DD HH:MM:SS' en UTC. Si elle n'est pas programmée, ne pas définir publishUp/publishDown et considérer un lancement direct.
        
        Description du workflow souhaité :
        ${form.workflowDescription || 'Le workflow doit inclure des étapes logiques comme des conditions d\'ouverture d\'email, des délais d\'attente, et des actions basées sur le comportement des utilisateurs.'}`;
      
      // Préparer les données de timing pour l'API avec ajustement du fuseau horaire
      const timingData = form.isPublished ? {
        startDate: adjustDateForTimezone(form.startDate, form.timezone),
        endDate: adjustDateForTimezone(form.endDate, form.timezone),
        timezone: 'UTC'
      } : null;

      

      // Appel à l'API pour générer le workflow
      const response = await api.post('/campaigns/generate-from-prompt', {
        prompt: prompt,
        timing: timingData,
        segments: form.selectedSegments.map(s => s.mautic_segment_id),
        emails: form.selectedEmails.map(e => e.mautic_email_id),
        mautic_url: mauticUrl,
        mautic_username: mauticUsername,
        mautic_password: mauticPassword
      });

      
      
      if (response.data.success) {
        // Extraire le workflow et le JSON de la campagne de la réponse
        const campaignJson = response.data.campaign_json;
        
        // Extraire une description du workflow à partir de la structure de la campagne
        let workflowDescription = "";
        
        // Construire une description narrative plus détaillée à partir de campaign_json
        workflowDescription = buildWorkflowNarrative(campaignJson);
        
        // Injecter timing s'il manque dans le JSON généré
        if (form.isPublished && campaignJson && campaignJson.campaign) {
          const startAdj = adjustDateForTimezone(form.startDate, form.timezone);
          const endAdj = adjustDateForTimezone(form.endDate, form.timezone);
          if (!campaignJson.campaign.publishUp && startAdj) campaignJson.campaign.publishUp = startAdj;
          if (!campaignJson.campaign.publishDown && endAdj) campaignJson.campaign.publishDown = endAdj;
        }
        
        // Mise à jour du workflow généré
        setForm(prev => ({
          ...prev,
          workflow:
            workflowDescription ||
            buildWorkflowNarrative(response.data.campaign_structure) ||
            response.data.campaign_structure?.description ||
            (campaignJson?.description || ''),
          // Ne pas écraser le graphique visuel s'il existe déjà
          campaignJson: prev.visualGraph || campaignJson
        }));
        
        // Génération d'un nom pour la campagne si non défini
        if (!customCampaignName && campaignJson?.campaign?.name) {
          setCustomCampaignName(campaignJson.campaign.name);
        } else if (!customCampaignName) {
          setCustomCampaignName(`Campagne ${form.selectedSegments[0].name} - ${form.selectedEmails[0].name}`);
        }
        
        setSuccess("Workflow généré avec succès!");
      } else {
        setError(response.data.message || "Erreur lors de la génération du workflow");
      }
    } catch (error) {
      console.error("Erreur lors de la génération du workflow:", error);
      setError("Erreur lors de la génération du workflow. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour créer un segment dans Unomi et Mautic
  const createSelectedSegment = async (segmentToCreate) => {
    // Si aucun segment n'est fourni, afficher une erreur
    if (!segmentToCreate) {
      setError('Aucun segment à créer');
      return;
    }

    // Ajouter l'ID du segment en cours de création à la liste des segments en chargement
    setLoadingSegmentIds(prev => [...prev, segmentToCreate.name]);
    setError('');
    
    try {
      // Créer une copie profonde de la configuration Unomi pour ne pas modifier l'original
      const modifiedUnomiConfig = JSON.parse(JSON.stringify(segmentToCreate.unomi_config || {}));
      
      // Ajouter la condition obligatoire pour properties.brand = [brand de l'utilisateur]
      const batiraCondition = {
        "type": "profilePropertyCondition",
        "parameterValues": {
          "propertyName": "properties.brand",
          "comparisonOperator": "equals",
          "propertyValue": userCredentials.brand || "batira" // Use the user's brand or fallback to "batira"
        }
      };
      
      // Insérer la condition batira au début du tableau de conditions
      if (modifiedUnomiConfig.condition && modifiedUnomiConfig.condition.type === 'booleanCondition') {
        if (!modifiedUnomiConfig.condition.parameterValues) {
          modifiedUnomiConfig.condition.parameterValues = { operator: 'and', subConditions: [] };
        }
        
        if (!modifiedUnomiConfig.condition.parameterValues.subConditions) {
          modifiedUnomiConfig.condition.parameterValues.subConditions = [];
        }
        
        // Ajouter la condition batira comme première condition dans le tableau subConditions
        modifiedUnomiConfig.condition.parameterValues.subConditions.unshift(batiraCondition);
      } else {
        // S'il n'y a pas de condition existante ou si ce n'est pas une condition booléenne, en créer une nouvelle
        const existingCondition = modifiedUnomiConfig.condition ? { ...modifiedUnomiConfig.condition } : null;
        
        modifiedUnomiConfig.condition = {
          type: 'booleanCondition',
          parameterValues: {
            operator: 'and',
            subConditions: [batiraCondition]
          }
        };
        
        // Ajouter la condition existante s'il y en avait une
        if (existingCondition) {
          modifiedUnomiConfig.condition.parameterValues.subConditions.push(existingCondition);
        }
      }
      
      // Créer le segment Unomi avec la configuration modifiée qui inclut la condition batira
      const unomiResponse = await api.post(
        '/segments/create',
        {
          segment_type: 'unomi',
          segment_config: modifiedUnomiConfig
        }
      );
      
      let creationMessages = [];
      let unomiSegmentId = null;
      let mauticSegmentId = null;
      
      if (unomiResponse.data.success) {
        creationMessages.push('Segment créé avec succès');
        // Stocker l'ID du segment Unomi si disponible
        unomiSegmentId = unomiResponse.data.segment_id || modifiedUnomiConfig.metadata?.id;
        
        // Mettre à jour le segment avec l'ID Unomi
        const updatedSegment = {...segmentToCreate};
        if (unomiSegmentId && updatedSegment.unomi_config && updatedSegment.unomi_config.metadata) {
          updatedSegment.unomi_config.metadata.id = unomiSegmentId;
        }
        
        // Créer le segment dans Mautic si disponible
        try {
          // Utiliser les identifiants Mautic récupérés du backend au lieu du localStorage
          const mauticUrl = userCredentials.mautic_url || '';
          const mauticUsername = userCredentials.mautic_username || '';
          const mauticPassword = userCredentials.mautic_password || '';
          
          // Préparer la configuration Mautic
          const mauticConfig = {...(segmentToCreate.mautic_config || {})};
          
          // Ajouter les identifiants Mautic à la configuration
          mauticConfig.mautic_url = mauticUrl;
          mauticConfig.mautic_username = mauticUsername;
          mauticConfig.mautic_password = mauticPassword;
          
          // Utiliser l'ID du segment Unomi pour le nom du tag si disponible
          if (!mauticConfig.tag_name && unomiSegmentId) {
            mauticConfig.tag_name = unomiSegmentId;
          } else if (!mauticConfig.tag_name) {
            mauticConfig.tag_name = mauticConfig.name;
          }
          
          // Créer le segment dans Mautic
          const mauticResponse = await api.post(
            '/segments/create',
            {
              segment_type: 'mautic',
              segment_config: mauticConfig
            }
          );
          
          if (mauticResponse.data.success) {
            // Extraire les informations du tag si disponibles
            const tagInfo = mauticResponse.data.tag ? 
              ` avec le tag '${mauticResponse.data.tag.tag}' (ID: ${mauticResponse.data.tag.id})` : '';
            creationMessages.push(`Segment créé avec succès${tagInfo}`);
            
            // Stocker l'ID du segment Mautic si disponible
            mauticSegmentId = mauticResponse.data.segment_id;
            
            // Mettre à jour le segment avec les IDs
            updatedSegment.unomi_segment_id = unomiSegmentId;
            updatedSegment.mautic_segment_id = mauticSegmentId;
            updatedSegment.isExisting = true; // Marquer comme existant maintenant
            if (mauticResponse.data.tag) {
              updatedSegment.mautic_tag_id = mauticResponse.data.tag.id;
            }
          } else {
            creationMessages.push(`Échec de la création du segment dans Mautic: ${mauticResponse.data.message || 'Erreur inconnue'}`);
          }
        } catch (mauticErr) {
          creationMessages.push(`Erreur lors de la création du segment dans Mautic: ${mauticErr.message}`);
        }
        
        // Mettre à jour le formulaire avec les segments mis à jour
        setForm(prev => {
          // Trouver l'index du segment dans le tableau des segments sélectionnés
          const segmentIndex = prev.selectedSegments.findIndex(s => s.name === updatedSegment.name);
          
          // Créer une copie du tableau des segments sélectionnés
          const updatedSegments = [...prev.selectedSegments];
          
          if (segmentIndex !== -1) {
            // Remplacer le segment existant par la version mise à jour
            updatedSegments[segmentIndex] = updatedSegment;
          } else {
            // Ajouter le segment s'il n'existe pas déjà
            updatedSegments.push(updatedSegment);
          }
          
          return {
            ...prev,
            selectedSegments: updatedSegments
          };
        });
        
        // Définir le message de succès global
        setSuccess(creationMessages.join('. '));
      } else {
        setError(unomiResponse.data.message || 'Échec de la création du segment');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Échec de la création du segment');
    } finally {
      // Supprimer le segment de la liste des segments en chargement
      setLoadingSegmentIds(prev => prev.filter(id => id !== segmentToCreate.name));
    }
  };

  // Fonction pour suggérer des emails basés sur l'objectif et le segment
  const suggestEmails = async () => {
    // Vérifier si l'objectif est défini
    if (!form.objectif.trim()) {
      setError('Veuillez définir un objectif avant de générer des emails');
      return;
    }
    
    // Vérifier si des segments sont sélectionnés ou si une cible est définie
    if (form.selectedSegments.length === 0 && !form.cible.trim()) {
      setError('Veuillez sélectionner au moins un segment ou définir une cible avant de générer des emails');
      return;
    }
    
    

    setIsLoadingEmails(true);
    setError('');
    setEmailSuggestions([]);
    try { console.debug('[EMAIL_SUGGEST][UI] Start', { objectif: form.objectif, selectedSegments: form.selectedSegments.map(s=>s.name), cible: form.cible }); } catch(_) {}

    try {
      // Formaté la requête pour que l'IA comprenne mieux
      const targetDescription = form.selectedSegments.length > 0
        ? form.selectedSegments.map(segment => `${segment.name}${segment.description ? ` (${segment.description})` : ''}`).join(', ')
        : form.cible;
      
      const formattedQuery = `Prompt Système - Assistant de Planification de Campagne Marketing
Tu es un expert en marketing automation et en création de workflows d'emails. Ta mission est de générer une séquence complète d'emails pour un workflow de campagne marketing structuré et logique.
Contexte
Objectif de campagne : ${form.objectif}
Segment(s) cible(s) : ${targetDescription}
RÈGLE FONDAMENTALE - HIÉRARCHIE STRICTE
PRIORITÉ ABSOLUE : La séquence d'emails doit être d'abord et avant tout conçue pour atteindre l'objectif de campagne de manière logique et progressive.
PRIORITÉ SECONDAIRE : Adapter le ton/style aux segments, SANS jamais compromettre la logique séquentielle de l'objectif.
Principe de cohérence séquentielle
UN SEUL PARCOURS LOGIQUE : Crée une séquence unique qui progresse vers l'objectif
SEGMENTS = ADAPTATION DE FORME : Les segments influencent uniquement le ton, vocabulaire et exemples utilisés
JAMAIS DE FRAGMENTATION : Ne crée pas d'emails différents par segment, mais des emails universels adaptés
Instructions de génération
1. Détermination du type de campagne
Évalue d'abord l'objectif pour déterminer le type de campagne optimal :
Campagne simple : Un seul email suffit (ex: promotion flash, annonce ponctuelle, newsletter)
Campagne workflow : Séquence d'emails nécessaire (ex: nurturing, onboarding, conversion complexe)
2. Structure selon le type
A. Pour une campagne simple
Génère un seul email optimisé qui :
Atteint directement l'objectif en une seule interaction
Maximise l'impact avec un message concentré
Inclut tous les éléments de persuasion nécessaires
B. Pour une campagne workflow
Génère une séquence d'emails qui forme un parcours client cohérent pour atteindre l'objectif défini. Cette séquence doit suivre une progression logique de l'engagement initial jusqu'à la conversion finale.
3. Approche créative selon la stratégie
Pour campagne simple :
Crée un email tout-en-un optimisé qui intègre tous les éléments persuasifs nécessaires pour atteindre l'objectif en une seule interaction.
Pour campagne workflow :
MÉTHODOLOGIE OBLIGATOIRE :
ÉTAPE 1 - Design de la séquence logique (ignorer les segments temporairement)
Identifie les 3-5 étapes nécessaires pour progresser de l'état actuel vers l'objectif
Crée une progression psychologique cohérente (sensibilisation → intérêt → désir → action)
Définis le rôle précis de chaque email dans cette progression
ÉTAPE 2 - Création des emails avec adaptation unifiée Chaque email doit :
Servir son rôle dans la progression vers l'objectif
Utiliser un ton/vocabulaire qui résonne avec TOUS les segments mentionnés
Inclure des exemples/références qui parlent à l'ensemble des segments
Maintenir la cohérence narrative avec l'email précédent
A. Séquence principale - Progression logique vers l'objectif : [Types d'emails déterminés par la logique de l'objectif, pas par les segments]
B. Emails de récupération - Pour gérer les non-engagés : [Emails de récupération alignés sur la même logique séquentielle]
4. Règles de génération - ORDRE STRICT
ÉTAPE 1 - Conception séquentielle : Définis d'abord la logique de progression vers l'objectif, INDÉPENDAMMENT des segments
ÉTAPE 2 - Adaptation stylistique : Ensuite seulement, adapte le ton et les exemples aux segments cibles
INTERDICTION ABSOLUE : Ne jamais créer des variantes d'emails par segment
COHÉRENCE TEMPORELLE : Chaque email doit s'appuyer sur le précédent pour construire vers l'objectif
TEST DE COHÉRENCE : La séquence doit fonctionner parfaitement même si on ignore les segments
5. Format de sortie requis
Pour campagne simple :
CAMPAGNE SIMPLE
Type : Envoi unique
Justification : [Pourquoi un seul email suffit]
 
EMAIL UNIQUE
Objet : [Ligne d'objet accrocheuse]
Contenu : [Corps de l'email complet et engageant]
CTA : [Appel à l'action clair et spécifique]
Pour campagne workflow :
CAMPAGNE WORKFLOW
Type : Séquence multi-emails
Justification : [Pourquoi plusieurs emails sont nécessaires]
Logique de progression : [Expliquer la progression psychologique vers l'objectif]
Adaptation segments : [Comment le ton/style est unifié pour tous les segments]
 
=== SÉQUENCE PRINCIPALE ===
EMAIL [N] - [TYPE BASÉ SUR LA PROGRESSION VERS L'OBJECTIF]
Rôle dans la progression : [Position dans la logique vers l'objectif]
Timing : [X jours après l'email précédent]
Objet : [Ligne d'objet accrocheuse, ton adapté aux segments]
Contenu : [Corps unifié qui progresse vers l'objectif, exemples multi-segments]
CTA : [Action cohérente avec la progression]
Lien avec email précédent : [Comment cet email s'appuie sur le précédent]
 
=== EMAILS DE RÉCUPÉRATION ===
EMAIL [N] - [TYPE DE RÉCUPÉRATION COHÉRENT AVEC LA SÉQUENCE]
Condition : [Déclencheur basé sur le non-engagement]
Timing : [Délai après la non-ouverture/non-clic]
Rôle : [Position dans la logique de récupération vers l'objectif]
Objet : [Approche différente mais cohérente avec l'objectif]
Contenu : [Récupération alignée sur la progression générale]
CTA : [Action adaptée au niveau de récupération]
6. Contraintes STRICTES
RÈGLE D'OR : Objectif d'abord, segments ensuite - JAMAIS l'inverse
INTERDICTION : Créer des emails différents par segment
OBLIGATION : Justifier la progression logique vers l'objectif
CAMPAGNE SIMPLE : 1 email unifié pour tous les segments
CAMPAGNE WORKFLOW :
Séquence principale : 3 à 5 emails dans une logique de progression claire
Emails de récupération : 2 à 3 emails cohérents avec la séquence principale
Total : Maximum 8 emails
TEST DE VALIDATION : La séquence doit raconter une histoire cohérente vers l'objectif
COHÉRENCE NARRATIVE : Chaque email doit référencer ou s'appuyer sur le précédent
ADAPTATION UNIFIÉE : Un seul ton/style qui fonctionne pour tous les segments
IMPÉRATIF : Commence par concevoir la progression logique vers l'objectif, puis unifie l'adaptation aux segments. Ne fragmente JAMAIS la séquence par segment.`;
      
      // Fetch LLM configuration from backend
      const llmConfig = await fetchLLMConfig();
      
      // Appel à l'API pour générer des suggestions d'emails
      let response;
      try {
        console.debug('[EMAIL_SUGGEST][REQ] POST /emails/suggest payload.sent');
        response = await api.post(
        '/emails/suggest',
        { 
          query: formattedQuery,
          provider: llmConfig.provider,
          model: llmConfig.model
        }
        );
        console.debug('[EMAIL_SUGGEST][RES] status', { status: response?.status, success: response?.data?.success, count: response?.data?.suggestions?.length });
      } catch (networkErr) {
        console.error('[EMAIL_SUGGEST][ERR][NETWORK]', networkErr);
        throw networkErr;
      }
      
      if (response.data.success && response.data.suggestions && response.data.suggestions.length > 0) {
        setEmailSuggestions(response.data.suggestions);
        setSuccess('Emails suggérés générés avec succès');
      } else {
        // Message d'erreur plus spécifique avec des conseils
        setError(response.data.message || 'Aucune suggestion d\'email trouvée. Essayez de décrire votre objectif et votre cible de manière plus détaillée.');
      }
    } catch (err) {
      console.error('[EMAIL_SUGGEST][ERR]', err);
      setError(err.message || 'Échec de la génération des suggestions d\'emails. Veuillez réessayer avec un objectif et une cible plus détaillés.');
    } finally {
      try { console.debug('[EMAIL_SUGGEST][UI] End'); } catch(_) {}
      setIsLoadingEmails(false);
    }
  };
  
  // Fonction pour créer un email spécifique
  const createSelectedEmail = async (emailToCreate) => {
    if (!emailToCreate) {
      setError('Email non spécifié');
      return;
    }

    // Ajouter l'email à la liste des emails en cours de création
    setForm(prev => ({
      ...prev,
      loadingEmailIds: [...prev.loadingEmailIds, emailToCreate.name]
    }));

    setError('');

    try {
      // Utiliser les identifiants Mautic récupérés du backend au lieu du localStorage
      const mauticUrl = userCredentials.mautic_url || '';
      const mauticUsername = userCredentials.mautic_username || '';
      const mauticPassword = userCredentials.mautic_password || '';
      
      // Ajouter les identifiants Mautic à la configuration de l'email
      const emailConfig = { ...emailToCreate.config };
      emailConfig.mautic_url = mauticUrl;
      emailConfig.mautic_username = mauticUsername;
      emailConfig.mautic_password = mauticPassword;
      
      const response = await api.post(
        '/emails/create',
        emailConfig
      );
      
      if (response.data.success) {
        const emailId = response.data.email_id;
        const emailName = response.data.email_name || emailToCreate.name;
        
        // Mettre à jour l'email avec l'ID Mautic
        const updatedEmail = {...emailToCreate};
        updatedEmail.mautic_email_id = emailId;
        updatedEmail.isExisting = true;
        
        if (response.data.email_config) {
          updatedEmail.mautic_config = response.data.email_config;
        }
        
        // Mettre à jour la liste des emails sélectionnés
        setForm(prev => {
          const emailIndex = prev.selectedEmails.findIndex(e => e.name === emailToCreate.name);
          const updatedEmails = [...prev.selectedEmails];
          
          if (emailIndex !== -1) {
            updatedEmails[emailIndex] = updatedEmail;
          }
          
          return {
            ...prev,
            selectedEmails: updatedEmails,
            loadingEmailIds: prev.loadingEmailIds.filter(id => id !== emailToCreate.name)
          };
        });
        
        setSuccess(`Email '${emailToCreate.name}' créé avec succès.`);
        
        // Sélectionner automatiquement l'email nouvellement créé
        handleSelectEmail(updatedEmail);

        // Optionnel : si vous voulez rafraîchir la liste des emails existants
        fetchExistingEmails();
      } else {
        setError(response.data.message || 'Échec de la création de l\'email');
        // Clear loading state on non-success responses as well
        setForm(prev => ({
          ...prev,
          loadingEmailIds: prev.loadingEmailIds.filter(id => id !== emailToCreate.name)
        }));
      }
    } catch (err) {
      console.error('Erreur lors de la création de l\'email:', err);
      setError(err.response?.data?.detail || err.message || 'Échec de la création de l\'email');
      
      // Retirer l'email de la liste des emails en cours de création en cas d'erreur
      setForm(prev => ({
        ...prev,
        loadingEmailIds: prev.loadingEmailIds.filter(id => id !== emailToCreate.name)
      }));
    }
  };

  const handleChange = (field) => (e) => {
    // Special handling for objectif field to reset other form data when it changes
    if (field === "objectif") {
      const newObjectif = e.target.value;
      // Reset other form fields if the objective has changed
      if (newObjectif !== form.objectif) {
        setForm({
          ...form,
          objectif: newObjectif,
          // Reset other fields
          canal: "mautic_email", // Reset canal to default
          cible: "",
          selectedSegments: [],
          contenu: "",
          selectedEmails: [],
          loadingEmailIds: [],
          timing: "",
          workflow: "",
          resume: "",
          useExistingSegment: false,
          useExistingEmail: false,
          startDate: '',
          endDate: '',
          isPublished: false,
          timezone: '+01:00',
          workflowDescription: '',
          campaignJson: null,
          visualGraph: null
        });
        
        // Reset any suggestions
        setSegmentSuggestions([]);
        setEmailSuggestions([]);
        
        // If changing the objective after having moved past step 1, show a message
        if (activeStep > 0) {
          setActiveStep(0); // Return to step 1
          setSuccess("Objectif modifié. Les données des autres étapes ont été réinitialisées.");
        }
      } else {
        // If objective text is the same, just update normally
        setForm({ ...form, [field]: e.target.value });
      }
    } else {
      // For other fields, update normally
      setForm({ ...form, [field]: e.target.value });
    }
  };

  // Render content for each step
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography sx={customStyles.stepTitle}>
              Définir l'objectif et le canal principal
            </Typography>
            {suggestions && showSuggestions && (
              <Collapse in={showSuggestions}>
                <Alert 
                  severity="info"
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => setShowSuggestions(false)}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2">
                    Suggestions IA pour la campagne "{campaignName}" :
                  </Typography>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    {suggestions.map((s, i) => (
                      <li key={i}>
                        <Typography variant="body2" color="text.secondary">
                          {s}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </Alert>
              </Collapse>
            )}
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="#868e96" mb={1} fontFamily="Montserrat, Arial, sans-serif">
                  Objectif de la campagne
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={4}
                  placeholder="Ex: Augmenter les ventes de produit X..."
                  value={form.objectif}
                  onChange={handleChange("objectif")}
                  sx={{
                    bgcolor: '#f8f9fa',
                    borderRadius: 2,
                    fontFamily: 'Montserrat, Arial, sans-serif',
                    fontSize: 15
                  }}
                  InputProps={{
                    style: { fontFamily: 'Montserrat, Arial, sans-serif' }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="#868e96" mb={1} fontFamily="Montserrat, Arial, sans-serif">
                  Canal principal
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={form.canal || 'mautic_email'}
                    onChange={(e) => setForm(prev => ({ ...prev, canal: e.target.value }))}
                    sx={{
                      bgcolor: '#f8f9fa',
                      borderRadius: 2,
                      fontFamily: 'Montserrat, Arial, sans-serif',
                      fontSize: 15
                    }}
                  >
                    <MenuItem value="mautic_email" sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                      Email
                    </MenuItem>
                    <MenuItem 
                      value="sms" 
                      disabled
                      sx={{ 
                        fontFamily: 'Montserrat, Arial, sans-serif',
                        color: '#9ca3af' 
                      }}
                    >
                      SMS (bientôt)
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="#868e96" fontFamily="Montserrat, Arial, sans-serif">
                Définissez clairement l'objectif de votre campagne pour que l'IA puisse vous suggérer des segments pertinents à l'étape suivante.
              </Typography>
            </Box>
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography sx={customStyles.stepTitle}>
              Quelle est la cible de la campagne ?
            </Typography>
            
            <Box sx={{ mb: 6 }}>
              <Typography variant="body2" color="#868e96" mb={2} fontFamily="Montserrat, Arial, sans-serif">
                Sélectionner un segment existant
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={filteredSegments}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={form.selectedSegments.filter(s => s.isExisting).map(s => ({ id: s.mautic_segment_id, name: s.name, description: s.description }))}
                  onChange={(_, newValue) => {
                    const selectedSegments = newValue.map(segment => ({
                        name: segment.name,
                        description: segment.description || '',
                        mautic_segment_id: segment.id,
                        isExisting: true
                      }));
                    setForm(prev => ({
                      ...prev,
                      selectedSegments: [
                        ...prev.selectedSegments.filter(s => !s.isExisting),
                        ...selectedSegments
                      ]
                    }));
                  }}
                  inputValue={segmentSearchText}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason !== 'input') return;
                    const searchValue = newInputValue || '';
                    setSegmentSearchText(searchValue);
                    if (!searchValue.trim()) {
                      setFilteredSegments(existingSegments);
                    } else {
                      const filtered = existingSegments.filter(segment => 
                        segment.name.toLowerCase().includes(searchValue.toLowerCase()) ||
                        (segment.description && segment.description.toLowerCase().includes(searchValue.toLowerCase()))
                      );
                      setFilteredSegments(filtered);
                    }
                  }}
                  clearOnBlur={false}
                  loading={isLoadingExistingSegments}
                  renderTags={() => null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Rechercher un segment..."
                      size="small"
                      autoComplete="off"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: '1rem' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'Montserrat, Arial, sans-serif', fontWeight: 500 }}>
                          {option.name}
                        </Typography>
                        {option.description && (
                          <Typography variant="caption" sx={{ color: '#6b7280', fontFamily: 'Montserrat, Arial, sans-serif' }}>
                            {option.description}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  sx={{
                    bgcolor: '#f8f9fa',
                    borderRadius: 2,
                    fontFamily: 'Montserrat, Arial, sans-serif',
                    fontSize: 15
                  }}
                />
                {isLoadingExistingSegments && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    <FormHelperText>Chargement des segments...</FormHelperText>
                  </Box>
                )}
                {!isLoadingExistingSegments && existingSegments.length === 0 && (
                  <FormHelperText error>Aucun segment disponible</FormHelperText>
                )}
                {/* Tags sous le champ pour les segments sélectionnés */}
                {form.selectedSegments.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {form.selectedSegments.map((segment, index) => (
                      <Chip
                        key={`${segment.name}-${index}`}
                        label={segment.name}
                        onDelete={() => handleSelectSegment(segment)}
                        size="small"
                        sx={{ 
                          fontFamily: 'Montserrat, Arial, sans-serif',
                          fontSize: '0.75rem',
                          height: '24px'
                        }}
                      />
                    ))}
                  </Box>
                )}
              </FormControl>
            </Box>
            
            <Box sx={{ mb: 6 }}>
              <Typography variant="body2" color="#868e96" mb={2} fontFamily="Montserrat, Arial, sans-serif">
                Ou demandez à l'IA
              </Typography>
              
              <Button
                onClick={suggestSegments}
                disabled={isLoadingSegments || !form.objectif.trim()}
                startIcon={isLoadingSegments ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                sx={{
                  ...customStyles.btn,
                  ...customStyles.btnSuggest,
                  fontFamily: 'Montserrat, Arial, sans-serif'
                }}
              >
                Suggérer un segment
              </Button>
              
              {!form.objectif.trim() && (
                <Typography variant="body2" color="error" sx={{ mt: 1, fontFamily: 'Montserrat, Arial, sans-serif' }}>
                  Veuillez d'abord définir un objectif pour générer des segments.
                </Typography>
              )}
            </Box>
            
            {segmentSuggestions.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom fontFamily="Montserrat, Arial, sans-serif">
                  Segments suggérés par l'IA
                </Typography>
                
                {segmentSuggestions.map((segment, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      ...customStyles.card,
                      mb: 2,
                      cursor: 'pointer',
                      border: form.selectedSegments.some(s => s.name === segment.name) ? '2px solid #007B8A' : '1px solid #e2e8f0',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#007B8A',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }
                    }}
                    onClick={() => handleSelectSegment(segment)}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" fontFamily="Montserrat, Arial, sans-serif" sx={{ color: '#1f2937' }}>
                        {segment.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {segment.isExisting ? (
                          <Chip 
                            label="Segment existant" 
                            color="success" 
                            size="small" 
                            sx={{ 
                              fontFamily: 'Montserrat, Arial, sans-serif',
                              fontSize: '0.75rem',
                              height: '20px'
                            }}
                          />
                        ) : (
                          <Chip 
                            label="Nouveau segment" 
                            color="primary" 
                            size="small" 
                            sx={{ 
                              fontFamily: 'Montserrat, Arial, sans-serif',
                              fontSize: '0.75rem',
                              height: '20px'
                            }}
                          />
                        )}
                        {form.selectedSegments.some(s => s.name === segment.name) && (
                          <Chip 
                            label="Sélectionné" 
                            color="info" 
                            size="small" 
                            sx={{ 
                              fontFamily: 'Montserrat, Arial, sans-serif',
                              fontSize: '0.75rem',
                              height: '20px'
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    <Typography variant="body2" color="#6b7280" paragraph fontFamily="Montserrat, Arial, sans-serif" sx={{ fontSize: '0.875rem' }}>
                      {segment.description}
                    </Typography>
                    {!segment.isExisting && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          createSelectedSegment(segment);
                        }}
                        disabled={loadingSegmentIds.includes(segment.name) || !mauticAvailable}
                        startIcon={loadingSegmentIds.includes(segment.name) ? <CircularProgress size={16} /> : null}
                        sx={{ 
                          fontFamily: 'Montserrat, Arial, sans-serif',
                          fontSize: '0.875rem',
                          padding: '0.25rem 0.75rem'
                        }}
                      >
                        Créer le segment
                      </Button>
                    )}
                  </Box>
                ))}
                
                {form.selectedSegments.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom fontFamily="Montserrat, Arial, sans-serif">
                      Segments sélectionnés :
                    </Typography>
                    {form.selectedSegments.map((segment, index) => (
                      <Typography key={index} variant="body1" gutterBottom fontFamily="Montserrat, Arial, sans-serif">
                        • {segment.name} {segment.isExisting ? '(Existant)' : '(Nouveau)'}
                      </Typography>
                    ))}
                    
                    {form.selectedSegments.map((segment, idx) => (
                      <Accordion key={idx} sx={{ mb: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography fontFamily="Montserrat, Arial, sans-serif">Configuration du segment: {segment.name}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <pre style={{ overflow: 'auto', maxHeight: '300px', fontFamily: 'monospace' }}>
                            {JSON.stringify(segment.unomi_config || {}, null, 2)}
                          </pre>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                    
                    {/* Le bouton global pour créer le segment a été supprimé car nous avons maintenant des boutons individuels pour chaque nouveau segment */}
                    
                    {!mauticAvailable && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        L'intégration Mautic n'est pas disponible car les identifiants ne sont pas configurés.
                      </Alert>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography sx={customStyles.stepTitle}>
              Préparer le Contenu de la Campagne
            </Typography>
            
            {/* Radio buttons pour choisir le mode - style identique au template HTML */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 3, 
              borderBottom: '1px solid #e2e8f0', 
              pb: 2, 
              mb: 2 
            }}>
              <FormControlLabel
                control={
                  <Radio
                    checked={!form.useExistingEmail}
                    onChange={() => setForm(prev => ({ ...prev, useExistingEmail: false }))}
                    sx={{ color: '#10B981' }}
                  />
                }
                label={
                  <Typography sx={{ 
                    fontFamily: 'Montserrat, Arial, sans-serif',
                    fontWeight: 500,
                    color: '#1f2937'
                  }}>
                    Générer par IA
                  </Typography>
                }
                sx={{ cursor: 'pointer' }}
              />
              <FormControlLabel
                control={
                  <Radio
                    checked={form.useExistingEmail}
                    onChange={() => setForm(prev => ({ ...prev, useExistingEmail: true }))}
                    sx={{ color: '#10B981' }}
                  />
                }
                label={
                  <Typography sx={{ 
                    fontFamily: 'Montserrat, Arial, sans-serif',
                    fontWeight: 500,
                    color: '#1f2937'
                  }}>
                    Sélectionner des templates
                  </Typography>
                }
                sx={{ cursor: 'pointer' }}
              />
            </Box>

            {/* Panneau IA */}
            {!form.useExistingEmail && (
              <Box>
                <Button
                  onClick={suggestEmails}
                  disabled={isLoadingEmails || !form.objectif.trim() || (!form.cible.trim() && form.selectedSegments.length === 0)}
                  startIcon={isLoadingEmails ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                  sx={{
                    ...customStyles.btn,
                    ...customStyles.btnSuggest,
                    fontFamily: 'Montserrat, Arial, sans-serif',
                    mb: 2
                  }}
                >
                  Générer une séquence d'emails
                </Button>
                
                {(!form.objectif.trim() || (!form.cible.trim() && form.selectedSegments.length === 0)) && (
                  <Typography variant="body2" color="error" sx={{ mt: 1, fontFamily: 'Montserrat, Arial, sans-serif' }}>
                    {!form.objectif.trim() 
                      ? "Veuillez d'abord définir un objectif pour générer des emails."
                      : "Veuillez sélectionner un segment ou définir une cible pour générer des emails."}
                  </Typography>
                )}

                {/* Container pour les suggestions IA */}
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {emailSuggestions.map((email, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        ...customStyles.card,
                        cursor: 'pointer',
                        border: form.selectedEmails.some(e => e.name === email.name) ? '2px solid #1976d2' : '1px solid #e2e8f0',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#1976d2',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }
                      }}
                      onClick={() => handleSelectEmail(email)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" fontFamily="Montserrat, Arial, sans-serif" sx={{ color: '#1f2937' }}>
                          {email.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {email.isExisting ? (
                            <Chip 
                              label="Email existant" 
                              color="success" 
                              size="small" 
                              sx={{ 
                                fontFamily: 'Montserrat, Arial, sans-serif',
                                fontSize: '0.75rem',
                                height: '20px'
                              }}
                            />
                          ) : (
                            <Chip 
                              label="Nouvel email" 
                              color="primary" 
                              size="small" 
                              sx={{ 
                                fontFamily: 'Montserrat, Arial, sans-serif',
                                fontSize: '0.75rem',
                                height: '20px'
                              }}
                            />
                          )}
                          {form.selectedEmails.some(e => e.name === email.name) && (
                            <Chip 
                              label="Sélectionné" 
                              color="info" 
                              size="small" 
                              sx={{ 
                                fontFamily: 'Montserrat, Arial, sans-serif',
                                fontSize: '0.75rem',
                                height: '20px'
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                      <Typography variant="body2" color="#6b7280" paragraph fontFamily="Montserrat, Arial, sans-serif" sx={{ fontSize: '0.875rem' }}>
                        {email.description}
                      </Typography>
                      {!email.isExisting && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            createSelectedEmail(email);
                          }}
                          disabled={form.loadingEmailIds.includes(email.name)}
                          startIcon={form.loadingEmailIds.includes(email.name) ? <CircularProgress size={16} /> : null}
                          sx={{ 
                            fontFamily: 'Montserrat, Arial, sans-serif',
                            fontSize: '0.875rem',
                            padding: '0.25rem 0.75rem'
                          }}
                        >
                          Créer l'email
                        </Button>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Panneau Templates */}
            {form.useExistingEmail && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'end', gap: 2, mb: 2 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" color="#868e96" mb={1} fontFamily="Montserrat, Arial, sans-serif">
                      Choisir un template
                    </Typography>
                    <FormControl fullWidth>
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={filteredEmails}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={form.selectedEmails
                          .filter(e => e.isExisting)
                          .map(e => {
                            const full = e.config || existingEmails.find(x => String(x.id) === String(e.mautic_email_id));
                            return full || { id: e.mautic_email_id, name: e.name, description: e.description };
                          })}
                        onChange={(_, newValue) => {
                          const selectedEmails = newValue.map(email => {
                            // email ici est l'option complète; conserver l'objet entier dans config
                            return {
                              name: email.name,
                              description: email.description || '',
                              mautic_email_id: email.id,
                              isExisting: true,
                              config: email
                            };
                          });
                          setForm(prev => ({
                            ...prev,
                            selectedEmails: [
                              ...prev.selectedEmails.filter(e => !e.isExisting),
                              ...selectedEmails
                            ]
                          }));
                        }}
                        inputValue={emailSearchText}
                        onInputChange={(_, newInputValue, reason) => {
                          if (reason !== 'input') return;
                          const searchValue = newInputValue || '';
                          setEmailSearchText(searchValue);
                          if (!searchValue.trim()) {
                            setFilteredEmails(existingEmails);
                          } else {
                            const filtered = existingEmails.filter(email => 
                              email.name.toLowerCase().includes(searchValue.toLowerCase()) ||
                              (email.subject && email.subject.toLowerCase().includes(searchValue.toLowerCase()))
                            );
                            setFilteredEmails(filtered);
                          }
                        }}
                        clearOnBlur={false}
                        loading={isLoadingExistingEmails}
                        renderTags={() => null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Rechercher un template d'email..."
                            size="small"
                            autoComplete="off"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ fontSize: '1rem' }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            <ListItemText
                              primary={option.name}
                              secondary={option.dateAdded && new Date(option.dateAdded).toLocaleDateString()}
                            />
                          </li>
                        )}
                        sx={{
                          bgcolor: '#f8f9fa',
                          borderRadius: 2,
                          fontFamily: 'Montserrat, Arial, sans-serif',
                          fontSize: 15
                        }}
                      />
                      {isLoadingExistingEmails && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          <FormHelperText>Chargement des templates d'email...</FormHelperText>
                        </Box>
                      )}
                      {!isLoadingExistingEmails && filteredEmails.length === 0 && (
                        <FormHelperText error>Aucun template d'email disponible</FormHelperText>
                      )}
                      {form.selectedEmails.filter(e => e.isExisting).length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                          {form.selectedEmails.filter(e => e.isExisting).map((email) => (
                            <Chip
                              key={email.mautic_email_id}
                              label={email.name}
                              color="primary"
                              variant="outlined"
                              onDelete={() => {
                                setForm(prev => ({
                                  ...prev,
                                  selectedEmails: prev.selectedEmails.filter(se => !(se.isExisting && se.mautic_email_id === email.mautic_email_id))
                                }));
                              }}
                              size="small"
                              sx={{ 
                                fontFamily: 'Montserrat, Arial, sans-serif',
                                fontSize: '0.75rem',
                                height: '24px'
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </FormControl>
                  </Box>
                  
                </Box>

                {/* Container pour les templates sélectionnés */}
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {form.selectedEmails.filter(e => e.isExisting).map((email, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        ...customStyles.card,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2
                      }}
                    >
                      <Box>
                        <Typography variant="h6" fontFamily="Montserrat, Arial, sans-serif" sx={{ color: '#1f2937' }}>
                          Séquence {index + 1}: {email.name}
                        </Typography>
                        <Typography variant="body2" color="#6b7280" fontFamily="Montserrat, Arial, sans-serif" sx={{ fontSize: '0.875rem' }}>
                          Ordre: {index + 1}
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          try {
                            const source = email.config || {};
                            const editorEmail = source.id ? source : {
                              id: email.mautic_email_id,
                              name: email.name,
                              customHtml: (email.config && email.config.customHtml) || undefined,
                              content: (email.config && email.config.content) || undefined
                            };
                            setEmailToEdit(editorEmail);
                            setEmailEditorOpen(true);
                          } catch (_) {
                            setEmailToEdit({ id: email.mautic_email_id, name: email.name });
                            setEmailEditorOpen(true);
                          }
                        }}
                        sx={{ 
                          fontFamily: 'Montserrat, Arial, sans-serif',
                          fontSize: '0.875rem',
                          padding: '0.25rem 0.75rem'
                        }}
                      >
                        <AutoAwesomeIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                        Éditer
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            
            {/* Section des emails sélectionnés - affichée pour les deux modes */}
            {form.selectedEmails.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom fontFamily="Montserrat, Arial, sans-serif">
                  Emails sélectionnés :
                </Typography>
                {form.selectedEmails.map((email, index) => (
                  <Typography key={index} variant="body1" gutterBottom fontFamily="Montserrat, Arial, sans-serif">
                    • {email.name} {email.isExisting ? '(Existant)' : '(Nouveau)'}
                  </Typography>
                ))}
                
                {form.selectedEmails.map((email, idx) => (
                  <Accordion key={idx} sx={{ mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography fontFamily="Montserrat, Arial, sans-serif">Configuration de l'email: {email.name}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <pre style={{ overflow: 'auto', maxHeight: '300px', fontFamily: 'monospace' }}>
                        {JSON.stringify(email.config || {}, null, 2)}
                      </pre>
                    </AccordionDetails>
                  </Accordion>
                ))}
                
                {!mauticAvailable && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    L'intégration Mautic n'est pas disponible car les identifiants ne sont pas configurés.
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        );
      case 3:
        return (
          <Box>
            <Typography sx={customStyles.stepTitle}>
              Planification de la Campagne
            </Typography>
            
            {/* Switch pour activer la planification - style identique au template HTML */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2 
            }}>
              <Typography sx={{ 
                fontFamily: 'Montserrat, Arial, sans-serif',
                fontWeight: 500,
                color: '#1f2937'
              }}>
                Activer la planification
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isPublished}
                    onChange={handlePublishToggle}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#007B8A',
                        '& + .MuiSwitch-track': {
                          backgroundColor: '#007B8A',
                        },
                      },
                    }}
                  />
                }
                label=""
                sx={{ m: 0 }}
              />
            </Box>

            {/* Panneau de planification - affiché seulement si activé */}
            {form.isPublished && (
              <Box sx={{ 
                mt: 3, 
                pt: 3, 
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="#868e96" mb={1} fontFamily="Montserrat, Arial, sans-serif">
                      Date de début
                    </Typography>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      value={form.startDate}
                      onChange={(e) => handleDateChange('startDate', e)}
                      sx={{
                        bgcolor: '#f8f9fa',
                        borderRadius: 2,
                        fontFamily: 'Montserrat, Arial, sans-serif',
                        fontSize: 15,
                        '& .MuiOutlinedInput-root': {
                          fontFamily: 'Montserrat, Arial, sans-serif'
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="#868e96" mb={1} fontFamily="Montserrat, Arial, sans-serif">
                      Date de fin (optionnel)
                    </Typography>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      value={form.endDate}
                      onChange={(e) => handleDateChange('endDate', e)}
                      sx={{
                        bgcolor: '#f8f9fa',
                        borderRadius: 2,
                        fontFamily: 'Montserrat, Arial, sans-serif',
                        fontSize: 15,
                        '& .MuiOutlinedInput-root': {
                          fontFamily: 'Montserrat, Arial, sans-serif'
                        }
                      }}
                      error={form.startDate && form.endDate && new Date(form.endDate) <= new Date(form.startDate)}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="body2" color="#868e96" mb={1} fontFamily="Montserrat, Arial, sans-serif">
                      Fuseau horaire
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={form.timezone}
                        onChange={handleTimezoneChange}
                        sx={{
                          bgcolor: '#f8f9fa',
                          borderRadius: 2,
                          fontFamily: 'Montserrat, Arial, sans-serif',
                          fontSize: 15
                        }}
                      >
                        <MenuItem value="Europe/Paris" sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                          (GMT+02:00) Paris
                        </MenuItem>
                        <MenuItem value="America/New_York" sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                          (GMT-04:00) New York
                        </MenuItem>
                        <MenuItem value="Asia/Tokyo" sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                          (GMT+09:00) Tokyo
                        </MenuItem>
                        <MenuItem value="UTC" sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                          UTC
                        </MenuItem>
                        <MenuItem value="+01:00" sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                          UTC+1 (Paris)
                        </MenuItem>
                        <MenuItem value="+02:00" sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                          UTC+2 (Helsinki)
                        </MenuItem>
                        <MenuItem value="+03:00" sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                          UTC+3 (Moscou)
                        </MenuItem>
                        <MenuItem value="+04:00" sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                          UTC+4 (Dubaï)
                        </MenuItem>
                        <MenuItem value="+05:00" sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                          UTC+5 (Karachi)
                        </MenuItem>
                        <MenuItem value="+05:30" sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                          UTC+5:30 (New Delhi)
                        </MenuItem>
                        <MenuItem value="+08:00" sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                          UTC+8 (Pékin)
                        </MenuItem>
                        <MenuItem value="+09:00" sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                          UTC+9 (Tokyo)
                        </MenuItem>
                        <MenuItem value="+10:00" sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                          UTC+10 (Sydney)
                        </MenuItem>
                        <MenuItem value="-05:00" sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                          UTC-5 (New York)
                        </MenuItem>
                        <MenuItem value="-06:00" sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                          UTC-6 (Chicago)
                        </MenuItem>
                        <MenuItem value="-07:00" sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                          UTC-7 (Denver)
                        </MenuItem>
                        <MenuItem value="-08:00" sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                          UTC-8 (Los Angeles)
                        </MenuItem>
                        <MenuItem value="-10:00" sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                          UTC-10 (Hawaii)
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        );
      case 4:
        return (
          <Box>
            <Typography sx={customStyles.stepTitle}>
              Construire le Workflow
            </Typography>
            
            {/* Sélecteur d'onglet - style identique au template HTML */}
            <Box sx={{ 
              display: 'flex', 
              borderBottom: '1px solid #e2e8f0', 
              mb: 2 
            }}>
              
              <Button
                variant="text"
                onClick={() => handleWorkflowModeChange('nl')}
                sx={{
                  ...customStyles.stepButton,
                  ...(form.workflowMode === 'nl' && { 
                    color: '#007B8A', 
                    borderBottom: '2px solid #007B8A',
                    borderRadius: 0
                  }),
                  fontFamily: 'Montserrat, Arial, sans-serif',
                  fontWeight: 500,
                  px: 3,
                  py: 1.5
                }}
              >
                Décrire avec l'IA
              </Button>
            </Box>

            

            {/* Panneau Langage Naturel */}
            {form.workflowMode === 'nl' && (
              <Box>
                <Typography variant="body2" color="#6b7280" mb={2} fontFamily="Montserrat, Arial, sans-serif" sx={{ fontSize: '0.875rem' }}>
                  Décrivez votre séquence ou cliquez sur "Suggérer" pour que l'IA la génère pour vous.
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="#868e96" mb={1} fontFamily="Montserrat, Arial, sans-serif">
                      Description du workflow
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={10}
                      value={form.workflowDescription}
                      onChange={(e) => setForm(prev => ({ ...prev, workflowDescription: e.target.value }))}
                      placeholder="- Envoyer l'email 'Bienvenue'..."
                      sx={{
                        bgcolor: '#f8f9fa',
                        borderRadius: 2,
                        fontFamily: 'Consolas, Monaco, monospace',
                        fontSize: '0.875rem',
                        '& .MuiOutlinedInput-root': {
                          fontFamily: 'Consolas, Monaco, monospace'
                        }
                      }}
                    />
                    <Button
                      onClick={generateCampaignWorkflow}
                      disabled={loading || form.selectedSegments.length === 0 || form.selectedEmails.length === 0}
                      startIcon={loading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                      sx={{
                        ...customStyles.btn,
                        ...customStyles.btnSuggest,
                        fontFamily: 'Montserrat, Arial, sans-serif',
                        mt: 2
                      }}
                    >
                      Suggérer un workflow
                    </Button>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    {/* Conteneur de prévisualisation du workflow */}
                    <Box sx={{ 
                      ...customStyles.card,
                      mt: 2
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        mb: 2 
                      }}>
                        <Typography variant="subtitle1" sx={{ 
                          fontFamily: 'Montserrat, Arial, sans-serif',
                          fontWeight: 600,
                          color: '#1f2937'
                        }}>
                          Prévisualisation du Json
                        </Typography>
                        <IconButton
                          size="small"
                          sx={{ 
                            color: '#6b7280',
                            '&:hover': { bgcolor: '#f1f5f9' }
                          }}
                        >
                          <CodeIcon sx={{ fontSize: '1.25rem' }} />
                        </IconButton>
                      </Box>
                      
                      {/* Vue Visuelle (par défaut) */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {form.workflow ? (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2, 
                            p: 1.5, 
                            bgcolor: '#f1f5f9', 
                            borderRadius: 1 
                          }}>
                            <CheckCircleIcon sx={{ fontSize: '1.25rem', color: '#6b7280' }} />
                            <Box>
                              <Typography variant="body2" sx={{ 
                                fontFamily: 'Montserrat, Arial, sans-serif',
                                fontWeight: 600,
                                color: '#1f2937',
                                fontSize: '0.875rem'
                              }}>
                                Workflow généré
                              </Typography>
                              <Typography variant="caption" sx={{ 
                                color: '#6b7280', 
                                fontFamily: 'Montserrat, Arial, sans-serif' 
                              }}>
                                {form.workflow.length > 100 ? form.workflow.substring(0, 100) + '...' : form.workflow}
                              </Typography>
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="#9ca3af" fontFamily="Montserrat, Arial, sans-serif" sx={{ fontSize: '0.875rem' }}>
                            La prévisualisation de votre workflow s'affichera ici...
                          </Typography>
                        )}
                      </Box>

                      {/* Vue JSON (cachée par défaut) */}
                      {(form.visualGraph || form.campaignJson) && (
                        <Box sx={{ 
                          mt: 2,
                          bgcolor: '#1f2937',
                          color: 'white',
                          borderRadius: 1,
                          p: 2,
                          overflow: 'auto',
                          maxHeight: '200px'
                        }}>
                          <pre style={{ 
                            fontFamily: 'Consolas, Monaco, monospace',
                            fontSize: '0.75rem',
                            margin: 0
                          }}>
                            {JSON.stringify(form.visualGraph || form.campaignJson, null, 2)}
                          </pre>
                        </Box>
                      )}
                    </Box>

                    {/* Description globale du workflow - vue textuelle détaillée */}
                    <Box sx={{ 
                      ...customStyles.card,
                      mt: 2
                    }}>
                      <Typography variant="subtitle1" sx={{ 
                        fontFamily: 'Montserrat, Arial, sans-serif',
                        fontWeight: 600,
                        color: '#1f2937'
                      }}>
                        Description globale du workflow
                      </Typography>
                      {form.workflow ? (
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1.5,
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'Montserrat, Arial, sans-serif',
                            color: '#1f2937',
                            fontSize: '0.9rem'
                          }}
                        >
                          {form.workflow}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          color="#9ca3af"
                          sx={{
                            mt: 1.5,
                            fontFamily: 'Montserrat, Arial, sans-serif',
                            fontSize: '0.875rem'
                          }}
                        >
                          La description détaillée de votre campagne s'affichera ici une fois le workflow généré. Elle expliquera, étape par étape, toutes les actions (ex: si l'email est ouvert ou non, quel email est envoyé ensuite, changements de segments, délais, conditions, etc.).
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        );
      case 5:
        return (
          <Box>
            <Typography sx={customStyles.stepTitle}>
              Résumé et Finalisation
            </Typography>
            
            {/* Résumé dans un style identique au template HTML */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2, 
              p: 3, 
              border: '1px solid #e2e8f0', 
              borderRadius: 2, 
              bgcolor: '#f8fafc' 
            }}>
              <Box sx={{ pb: 2, borderBottom: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle2" sx={{ 
                  fontFamily: 'Montserrat, Arial, sans-serif',
                  fontWeight: 600,
                  color: '#4b5563'
                }}>
                  Objectif & Canal
                </Typography>
                <Typography sx={{ 
                  color: '#1f2937', 
                  mt: 0.5,
                  fontFamily: 'Montserrat, Arial, sans-serif'
                }}>
                  {form.objectif || <span style={{color:'#bdbdbd'}}>Non défini</span>} | Canal: {form.canal === 'mautic_email' ? 'Email' : form.canal === 'sms' ? 'SMS' : 'Non défini'}
                </Typography>
                <Typography sx={{ 
                  color: '#1f2937', 
                  mt: 0.5,
                  fontFamily: 'Montserrat, Arial, sans-serif'
                }}>
                  Nom de la campagne: {(form.campaignJson?.campaign?.name) || customCampaignName || campaignName || <span style={{color:'#bdbdbd'}}>Non défini</span>}
                </Typography>
              </Box>
              
              <Box sx={{ pb: 2, borderBottom: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle2" sx={{ 
                  fontFamily: 'Montserrat, Arial, sans-serif',
                  fontWeight: 600,
                  color: '#4b5563'
                }}>
                  Cible
                </Typography>
                <Typography sx={{ 
                  color: '#1f2937', 
                  mt: 0.5,
                  fontFamily: 'Montserrat, Arial, sans-serif'
                }}>
                  {form.selectedSegments?.length > 0 ? 
                    form.selectedSegments.map(s => s.name).join(', ') : 
                    <span style={{color:'#bdbdbd'}}>Non défini</span>
                  }
                </Typography>
              </Box>
              
              <Box sx={{ pb: 2, borderBottom: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle2" sx={{ 
                  fontFamily: 'Montserrat, Arial, sans-serif',
                  fontWeight: 600,
                  color: '#4b5563'
                }}>
                  Contenu
                </Typography>
                <Typography sx={{ 
                  color: '#1f2937', 
                  mt: 0.5,
                  fontFamily: 'Montserrat, Arial, sans-serif'
                }}>
                  {form.selectedEmails?.length > 0 ? 
                    form.selectedEmails.map(e => e.name).join(', ') : 
                    <span style={{color:'#bdbdbd'}}>Non défini</span>
                  }
                </Typography>
              </Box>
              
              <Box sx={{ pb: 2, borderBottom: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle2" sx={{ 
                  fontFamily: 'Montserrat, Arial, sans-serif',
                  fontWeight: 600,
                  color: '#4b5563'
                }}>
                  Workflow
                </Typography>
                <Typography sx={{ 
                  color: '#1f2937', 
                  mt: 0.5,
                  fontFamily: 'Montserrat, Arial, sans-serif',
                  whiteSpace: 'pre-wrap'
                }}>
                  {form.workflow || <span style={{color:'#bdbdbd'}}>Non défini</span>}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  fontFamily: 'Montserrat, Arial, sans-serif',
                  fontWeight: 600,
                  color: '#4b5563'
                }}>
                  Planification
                </Typography>
                <Typography sx={{ 
                  color: '#1f2937', 
                  mt: 0.5,
                  fontFamily: 'Montserrat, Arial, sans-serif'
                }}>
                  {form.isPublished ? (
                    (() => {
                      const deb = formatDisplayDate(form.startDate);
                      const fin = formatDisplayDate(form.endDate);
                      const parts = [];
                      parts.push(`Début: ${deb || 'Non défini'}`);
                      parts.push(`Fin: ${fin || 'Non définie'}`);
                      parts.push(`Fuseau: ${form.timezone || 'UTC'}`);
                      return `Campagne planifiée. ${parts.join(', ')}.`;
                    })()
                  ) : (
                    'Campagne non planifiée (envoi manuel ou lancement direct).'
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };
  
  // Fonction pour vérifier si l'étape actuelle est valide
  const isStepValid = (step) => {
    switch (step) {
      case 0: // Étape Objectif
        return form.objectif.trim() !== "";
      case 1: // Étape Cible
        return form.selectedSegments.length > 0;
      case 2: // Étape Contenu
        return form.selectedEmails.length > 0;
      case 3: // Étape Timing
        return true; // Le timing n'est pas obligatoire
      case 4: // Étape Workflow
        return form.workflow.trim() !== "" && (form.campaignJson !== null || form.visualGraph !== null);
      case 5: // Étape Résumé
        return form.selectedSegments.length > 0 && form.selectedEmails.length > 0;
      default:
        return false;
    }
  };

  // Détermine l'étape maximale atteignable en validant séquentiellement depuis le début
  const getMaxReachableStep = () => {
    let maxReachable = 0;
    for (let i = 0; i < steps.length - 1; i++) {
      if (isStepValid(i)) {
        maxReachable = i + 1; // l'étape suivante devient atteignable
      } else {
        break; // on s'arrête dès que l'étape courante n'est pas valide
      }
    }
    return maxReachable;
  };

  // Fonction pour passer à l'étape suivante
  const handleNext = () => {
    if (isStepValid(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else {
      // Afficher un message d'erreur spécifique à l'étape
      switch (activeStep) {
        case 0:
          setError('Veuillez définir un objectif avant de continuer.');
          break;
        case 1:
          setError('Veuillez sélectionner au moins un segment avant de continuer.');
          break;
        case 2:
          setError('Veuillez sélectionner au moins un email avant de continuer.');
          break;
        case 4:
          setError('Veuillez générer un workflow avant de continuer.');
          break;
        default:
          setError('Veuillez compléter cette étape avant de continuer.');
      }
    }
  };

  // Fonction pour revenir à l'étape précédente
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Fonction pour fermer les alertes
  const handleCloseAlert = () => {
    setError('');
    setSuccess('');
  };

  // Fonction pour créer la campagne directement à partir du JSON généré
  const handleCreateCampaign = async () => {
    // Vérifier que nous avons un JSON de campagne (soit généré par IA, soit créé visuellement)
    const campaignData = form.visualGraph || form.campaignJson;
    if (!campaignData) {
      setError('Aucun workflow généré. Veuillez d\'abord générer un workflow ou créer un schéma visuel.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      console.log('Création de campagne avec le JSON généré:', campaignData);

      // Ajuster les dates dans le JSON de la campagne si nécessaire
      let adjustedCampaignJson = {...campaignData};
      
      // Ajuster les dates de la campagne si elles existent
      if (form.isPublished && adjustedCampaignJson.campaign && adjustedCampaignJson.campaign.publishUp) {
        adjustedCampaignJson.campaign.publishUp = adjustDateForTimezone(adjustedCampaignJson.campaign.publishUp, form.timezone);
        console.log(`Date de début ajustée : ${adjustedCampaignJson.campaign.publishUp}`);
      }
      
      if (form.isPublished && adjustedCampaignJson.campaign && adjustedCampaignJson.campaign.publishDown) {
        adjustedCampaignJson.campaign.publishDown = adjustDateForTimezone(adjustedCampaignJson.campaign.publishDown, form.timezone);
        console.log(`Date de fin ajustée : ${adjustedCampaignJson.campaign.publishDown}`);
      }
      
      // Ajuster également les dates des événements si nécessaire
      if (form.isPublished && adjustedCampaignJson.campaign && adjustedCampaignJson.campaign.events) {
        adjustedCampaignJson.campaign.events.forEach(event => {
          // Ajuster la date de déclenchement si elle existe
          if (event.triggerDate) {
            const oldDate = event.triggerDate;
            event.triggerDate = adjustDateForTimezone(event.triggerDate, form.timezone);
            console.log(`Événement - date de déclenchement ajustée: ${oldDate} -> ${event.triggerDate}`);
          }
          
          // Ajuster les dates de timing si elles existent
          if (event.triggerMode === 'date' && event.triggerDate) {
            const oldDate = event.triggerDate;
            event.triggerDate = adjustDateForTimezone(event.triggerDate, form.timezone);
            console.log(`Événement - mode date ajusté: ${oldDate} -> ${event.triggerDate}`);
          }
          
          // Vérifier les propriétés spécifiques qui pourraient contenir des dates
          for (const prop in event) {
            if (typeof event[prop] === 'string' && 
                (event[prop].includes('-') || event[prop].includes(':')) && 
                /\d{4}-\d{2}-\d{2}/.test(event[prop])) {
              try {
                const oldDate = event[prop];
                event[prop] = adjustDateForTimezone(event[prop], form.timezone);
                console.log(`Événement - propriété date ajustée: ${prop}: ${oldDate} -> ${event[prop]}`);
              } catch (err) {
                console.log(`Erreur lors de l'ajustement de la date pour la propriété ${prop}:`, err);
              }
            }
          }
        });
      }

      // S'assurer que les dates sont présentes si planifié, même si absentes initialement
      if (form.isPublished && adjustedCampaignJson.campaign) {
        const startAdj = adjustDateForTimezone(form.startDate, form.timezone);
        const endAdj = adjustDateForTimezone(form.endDate, form.timezone);
        if (!adjustedCampaignJson.campaign.publishUp && startAdj) adjustedCampaignJson.campaign.publishUp = startAdj;
        if (!adjustedCampaignJson.campaign.publishDown && endAdj) adjustedCampaignJson.campaign.publishDown = endAdj;
      }

      // Appel à l'API pour créer la campagne avec les identifiants Mautic du backend
      const response = await api.post(
        '/campaigns/create-from-json',
        { 
          campaign_json: adjustedCampaignJson,
          mautic_url: userCredentials.mautic_url,
          mautic_username: userCredentials.mautic_username,
          mautic_password: userCredentials.mautic_password
        }
      );
      
      if (response.data.success) {
        const campaignId = response.data.campaign_id || response.data.data?.campaign?.id || 'ID non disponible';
        setSuccess(`Campagne créée avec succès! (ID: ${campaignId})`);
        
        // Rediriger vers une page de succès
        navigate('/campagne/succes', { 
          state: { 
            campaignId, 
            campaignName: adjustedCampaignJson?.campaign?.name || customCampaignName || 'Nouvelle campagne',
            message: `Campagne créée avec succès! (ID: ${campaignId})`
          } 
        });
      } else {
        setError('veuillez regénérer le workflow s\'il vous plaît');
      }
    } catch (err) {
      console.error('Erreur lors de la création de la campagne:', err);
      setError('veuillez regénérer le workflow s\'il vous plaît');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={customStyles.root}>
      <Box sx={customStyles.container}>
        {/* Header */}
        <Box sx={customStyles.header}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                color: '#6b7280',
                '&:hover': {
                  backgroundColor: 'rgba(0, 123, 138, 0.1)',
                  color: '#007B8A'
                }
              }}
              title="Retour à la page précédente"
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography sx={customStyles.headerTitle}>
                Assistant de Planification de Campagne
              </Typography>
              <Typography sx={customStyles.headerSubtitle}>
                Suivez les étapes pour construire un plan de campagne solide.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Navigation Steps */}
        <Box sx={customStyles.navigation}>
          {steps.map((step, index) => {
            const maxReachable = getMaxReachableStep();
            const isClickable = index <= maxReachable; // on ne peut pas aller au-delà
            const isActive = activeStep === index;
            return (
              <Button
                key={step}
                disabled={!isClickable}
                sx={{
                  ...customStyles.stepButton,
                  ...(isActive && { color: '#fff', backgroundColor: '#007B8A' }),
                  ...(!isClickable && {
                    color: '#9ca3af',
                    cursor: 'not-allowed',
                    opacity: 0.6,
                    borderColor: '#e5e7eb'
                  })
                }}
                onClick={() => isClickable && setActiveStep(index)}
              >
                {index + 1}. {step}
              </Button>
            );
          })}
        </Box>

        {/* Content */}
        <Box sx={customStyles.content}>
          {renderStepContent()}

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 8 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0}
              sx={{
                ...customStyles.btn,
                ...customStyles.btnSecondary,
                borderRadius: 2,
                fontWeight: 600
              }}
            >
              Retour
            </Button>
            <Button
              variant="contained"
              onClick={activeStep === steps.length - 1 ? handleCreateCampaign : handleNext}
              disabled={!isStepValid(activeStep) || (activeStep === steps.length - 1 && loading)}
              startIcon={activeStep === steps.length - 1 && loading ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                ...customStyles.btn,
                ...customStyles.btnPrimary,
                borderRadius: 2,
                fontWeight: 600
              }}
            >
              {activeStep === steps.length - 1 
                ? (loading ? 'Création en cours...' : 'Terminer') 
                : 'Suivant'}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Messages d'erreur et de succès */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {emailEditorOpen && (
        <EmailAssistant
          open={emailEditorOpen}
          onClose={() => setEmailEditorOpen(false)}
          mode="edit"
          email={emailToEdit}
          onContentChange={(html) => {
            try {
              const editedId = (emailToEdit && emailToEdit.id) || null;
              if (!editedId) return;
              setForm(prev => ({
                ...prev,
                selectedEmails: prev.selectedEmails.map(se => {
                  const currentId = se.mautic_email_id || se.id;
                  if (currentId === editedId) {
                    const updatedConfig = { ...(se.config || {}), customHtml: html };
                    return { ...se, config: updatedConfig };
                  }
                  return se;
                })
              }));
            } catch (_) {}
          }}
        />
      )}
    </Box>
  );
}
